import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { formatAnalyticsDate, formatAnalyticsAxisDate } from '../../../../shared/components/sette/chart-theme';

export interface DateRange {
	startDate: Date;
	endDate: Date;
	label?: string;
	presetKey?: string;
}

export interface CompareOption {
	type: 'previous_period' | 'same_period_last_year' | 'custom' | 'none';
	label: string;
	startDate?: Date;
	endDate?: Date;
}

interface QuickPreset {
	key: string;
	labelKey: string;
	getRange: () => { start: Date; end: Date };
}

interface CalendarDay {
	date: Date;
	isCurrentMonth: boolean;
}

interface MonthData {
	year: number;
	month: number;
	days: CalendarDay[];
}

const MONTH_KEYS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const WEEKDAY_KEYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

// Delegates to chart-theme's shared lang-aware date formatter (mk/al: "14.10.2026", en: "14 Oct
// 2026") so a date-range label matches every other date shown across Analytics (KPI cards, chart
// axes) instead of the hardcoded-English month abbreviations this component used to have of its
// own. Re-exported under their old names since filter-bar.component.ts and saved-view.util.ts
// already import them from here.
export const formatDisplayDate = formatAnalyticsDate;
export const formatShortDate = formatAnalyticsAxisDate;

export function formatRangeText(start: Date, end: Date, lang?: string): string {
	if (start.getFullYear() === end.getFullYear()) {
		if (start.getMonth() === end.getMonth() && start.getDate() === end.getDate()) {
			return formatDisplayDate(start, lang);
		}
		return `${formatShortDate(start, lang)} – ${formatDisplayDate(end, lang)}`;
	}
	return `${formatDisplayDate(start, lang)} – ${formatDisplayDate(end, lang)}`;
}

export function getDaysDifference(start: Date, end: Date): number {
	const diffTime = Math.abs(end.getTime() - start.getTime());
	return Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
}

// vsLabel/lang come from the caller's own TranslateService (this is a free function, no DI of its
// own) - every call site in this module passes translate.instant('ANALYTICS_DATE_PICKER.VS') and
// translate.currentLang. The English default only matters if some future caller forgets to pass it.
export function calculatePreviousPeriod(start: Date, end: Date, lang?: string, vsLabel = 'vs'): { start: Date; end: Date; label: string } {
	const days = getDaysDifference(start, end);
	const prevEnd = new Date(start.getTime() - 24 * 60 * 60 * 1000);
	const prevStart = new Date(prevEnd.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
	return { start: prevStart, end: prevEnd, label: `${vsLabel} ${formatRangeText(prevStart, prevEnd, lang)}` };
}

export function calculateSamePeriodLastYear(start: Date, end: Date, lang?: string, vsLabel = 'vs'): { start: Date; end: Date; label: string } {
	const lyStart = new Date(start);
	lyStart.setFullYear(start.getFullYear() - 1);
	const lyEnd = new Date(end);
	lyEnd.setFullYear(end.getFullYear() - 1);
	return { start: lyStart, end: lyEnd, label: `${vsLabel} ${formatRangeText(lyStart, lyEnd, lang)}` };
}

function startOfMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function startOfQuarter(date: Date): Date {
	return new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);
}

function endOfQuarter(date: Date): Date {
	return new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3 + 3, 0);
}

function startOfToday(): Date {
	const now = new Date();
	return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

// "This month/quarter/year" otherwise always reaches the calendar period's last day (e.g.
// 31.12), even when today is earlier (e.g. 28.10) - the extra, not-yet-happened days then (a)
// get averaged into the trend as empty buckets, artificially pulling the average down, and (b)
// make the "previous period" comparison (calculatePreviousPeriod derives its day-count from this
// same start/end) pull in a full prior period instead of the same number of days-to-date, so a
// partial current period can come out looking worse than it is. Only ever pulls the end earlier,
// never later - a genuinely past "this_month" (e.g. viewed on the 1st of the next month) is
// already <= today and is returned unchanged.
function clampEndToToday(end: Date): Date {
	const today = startOfToday();
	return end > today ? today : end;
}

// All presets resolve relative to "today" (real date), not a fixed sample reference date.
// labelKey is translated at point of use (template pipe, or translate.instant when the string is
// needed in TS, e.g. applyRange()) - never stored as literal text here.
export const QUICK_PRESETS: QuickPreset[] = [
	{
		key: 'last_7_days',
		labelKey: 'ANALYTICS_DATE_PICKER.PRESET_LAST_7_DAYS',
		getRange: () => {
			const end = new Date();
			const start = new Date(end);
			start.setDate(end.getDate() - 6);
			return { start, end };
		},
	},
	{
		key: 'last_30_days',
		labelKey: 'ANALYTICS_DATE_PICKER.PRESET_LAST_30_DAYS',
		getRange: () => {
			const end = new Date();
			const start = new Date(end);
			start.setDate(end.getDate() - 29);
			return { start, end };
		},
	},
	{ key: 'this_month', labelKey: 'ANALYTICS_DATE_PICKER.PRESET_THIS_MONTH', getRange: () => ({ start: startOfMonth(new Date()), end: clampEndToToday(endOfMonth(new Date())) }) },
	{
		key: 'last_month',
		labelKey: 'ANALYTICS_DATE_PICKER.PRESET_LAST_MONTH',
		getRange: () => {
			const lastMonth = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
			return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
		},
	},
	{ key: 'this_quarter', labelKey: 'ANALYTICS_DATE_PICKER.PRESET_THIS_QUARTER', getRange: () => ({ start: startOfQuarter(new Date()), end: clampEndToToday(endOfQuarter(new Date())) }) },
	{
		key: 'last_quarter',
		labelKey: 'ANALYTICS_DATE_PICKER.PRESET_LAST_QUARTER',
		getRange: () => {
			const thisQuarterStart = startOfQuarter(new Date());
			const lastQuarterEnd = new Date(thisQuarterStart.getTime() - 24 * 60 * 60 * 1000);
			return { start: startOfQuarter(lastQuarterEnd), end: lastQuarterEnd };
		},
	},
	{ key: 'this_year', labelKey: 'ANALYTICS_DATE_PICKER.PRESET_THIS_YEAR', getRange: () => ({ start: new Date(new Date().getFullYear(), 0, 1), end: clampEndToToday(new Date(new Date().getFullYear(), 11, 31)) }) },
	{ key: 'last_year', labelKey: 'ANALYTICS_DATE_PICKER.PRESET_LAST_YEAR', getRange: () => ({ start: new Date(new Date().getFullYear() - 1, 0, 1), end: new Date(new Date().getFullYear() - 1, 11, 31) }) },
];

// Ported from analytics-design/src/components/DateRangePicker.tsx. Same state machine, same
// quick presets, same two-month calendar + compare-period logic - just Angular idioms
// (class fields instead of useState, a document:mousedown HostListener instead of the two
// useEffect outside-click handlers).
@Component({
	selector: 'app-date-range-picker',
	standalone: true,
	imports: [NgTemplateOutlet, MatIconModule, TranslateModule],
	templateUrl: './date-range-picker.component.html',
	styleUrl: './date-range-picker.component.scss',
})
export class DateRangePickerComponent implements OnInit {
	constructor(private readonly translate: TranslateService) {}

	@Input() dateRange!: DateRange;
	@Output() dateRangeChange = new EventEmitter<DateRange>();
	// Parameter properties (constructor above) are assigned before field initializers run, so
	// this.translate is already set here.
	@Input() compareOption: CompareOption = { type: 'none', label: this.translate.instant('ANALYTICS_DATE_PICKER.NO_COMPARISON') };
	@Output() compareOptionChange = new EventEmitter<CompareOption>();
	@Input() showCompare = true;

	get monthNames(): string[] {
		return MONTH_KEYS.map((k) => this.translate.instant(`ANALYTICS_DATE_PICKER.MONTH_${k}`));
	}

	get weekdayNames(): string[] {
		return WEEKDAY_KEYS.map((k) => this.translate.instant(`ANALYTICS_DATE_PICKER.WEEKDAY_${k}`));
	}

	readonly quickPresets = QUICK_PRESETS;

	private get lang(): string {
		return this.translate.currentLang;
	}

	private get vsLabel(): string {
		return this.translate.instant('ANALYTICS_DATE_PICKER.VS');
	}

	isOpen = false;
	isCompareOpen = false;
	isCustomComparePickerOpen = false;

	viewMonth!: Date;
	tempStart!: Date;
	tempEnd: Date | null = null;
	hoverDate: Date | null = null;
	activePreset: string | null = null;

	compareViewMonth!: Date;
	compareTempStart!: Date;
	compareTempEnd: Date | null = null;
	compareHoverDate: Date | null = null;

	@ViewChild('pickerContainer') pickerContainer?: ElementRef<HTMLElement>;
	@ViewChild('compareContainer') compareContainer?: ElementRef<HTMLElement>;

	ngOnInit(): void {
		this.tempStart = this.dateRange.startDate;
		this.tempEnd = this.dateRange.endDate;
		this.viewMonth = new Date(this.dateRange.startDate.getFullYear(), this.dateRange.startDate.getMonth(), 1);
		this.compareTempStart = this.dateRange.startDate;
		this.compareTempEnd = this.dateRange.endDate;
		this.compareViewMonth = new Date(this.dateRange.startDate.getFullYear(), this.dateRange.startDate.getMonth(), 1);
	}

	@HostListener('document:mousedown', ['$event'])
	onDocumentMousedown(event: MouseEvent): void {
		const target = event.target as Node;
		if (this.pickerContainer && !this.pickerContainer.nativeElement.contains(target)) {
			this.isOpen = false;
		}
		if (this.compareContainer && !this.compareContainer.nativeElement.contains(target)) {
			this.isCompareOpen = false;
			this.isCustomComparePickerOpen = false;
		}
	}

	get triggerLabel(): string {
		return this.dateRange.label || formatRangeText(this.dateRange.startDate, this.dateRange.endDate, this.lang);
	}

	get prevPeriod() {
		return calculatePreviousPeriod(this.dateRange.startDate, this.dateRange.endDate, this.lang, this.vsLabel);
	}

	get lastYearPeriod() {
		return calculateSamePeriodLastYear(this.dateRange.startDate, this.dateRange.endDate, this.lang, this.vsLabel);
	}

	get activeCompareLabel(): string {
		const c = this.compareOption;
		if (c.type === 'none') return this.translate.instant('ANALYTICS_DATE_PICKER.NO_COMPARISON');
		if (c.type === 'previous_period') return this.prevPeriod.label;
		if (c.type === 'same_period_last_year') return this.lastYearPeriod.label;
		if (c.type === 'custom' && c.startDate && c.endDate) return `${this.vsLabel} ${formatRangeText(c.startDate, c.endDate, this.lang)}`;
		return c.label || this.prevPeriod.label;
	}

	get activeCompareLabelShort(): string {
		return this.activeCompareLabel.replace(/^\S+\s*/, '');
	}

	// Exposed for the template (custom-compare menu entry's preview text) - free functions
	// can't be called directly from an Angular template.
	formatRange = (start: Date, end: Date) => formatRangeText(start, end, this.lang);

	get modalRangePreviewText() {
		const start = this.tempStart;
		const end = this.tempEnd || this.hoverDate || this.tempStart;
		const minD = start <= end ? start : end;
		const maxD = start <= end ? end : start;
		return { text: `${formatDisplayDate(minD, this.lang)} – ${formatDisplayDate(maxD, this.lang)}`, days: getDaysDifference(minD, maxD) };
	}

	get compareModalRangePreviewText() {
		const start = this.compareTempStart;
		const end = this.compareTempEnd || this.compareHoverDate || this.compareTempStart;
		const minD = start <= end ? start : end;
		const maxD = start <= end ? end : start;
		return { text: `${formatDisplayDate(minD, this.lang)} – ${formatDisplayDate(maxD, this.lang)}`, days: getDaysDifference(minD, maxD) };
	}

	get leftMonthData(): MonthData {
		return this.buildMonthDays(this.viewMonth);
	}

	get rightMonthData(): MonthData {
		return this.buildMonthDays(new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + 1, 1));
	}

	get compareLeftMonthData(): MonthData {
		return this.buildMonthDays(this.compareViewMonth);
	}

	get compareRightMonthData(): MonthData {
		return this.buildMonthDays(new Date(this.compareViewMonth.getFullYear(), this.compareViewMonth.getMonth() + 1, 1));
	}

	toggleOpen(): void {
		this.isOpen = !this.isOpen;
		this.isCompareOpen = false;
		this.isCustomComparePickerOpen = false;
		if (this.isOpen) {
			this.tempStart = this.dateRange.startDate;
			this.tempEnd = this.dateRange.endDate;
			this.hoverDate = null;
			this.activePreset = this.dateRange.presetKey || null;
			this.viewMonth = new Date(this.dateRange.startDate.getFullYear(), this.dateRange.startDate.getMonth(), 1);
		}
	}

	toggleCompareOpen(): void {
		this.isCompareOpen = !this.isCompareOpen;
		this.isOpen = false;
	}

	prevMonth(isCompare = false): void {
		if (isCompare) {
			this.compareViewMonth = new Date(this.compareViewMonth.getFullYear(), this.compareViewMonth.getMonth() - 1, 1);
		} else {
			this.viewMonth = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() - 1, 1);
		}
	}

	nextMonth(isCompare = false): void {
		if (isCompare) {
			this.compareViewMonth = new Date(this.compareViewMonth.getFullYear(), this.compareViewMonth.getMonth() + 1, 1);
		} else {
			this.viewMonth = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + 1, 1);
		}
	}

	prevYear(isCompare = false): void {
		if (isCompare) {
			this.compareViewMonth = new Date(this.compareViewMonth.getFullYear() - 1, this.compareViewMonth.getMonth(), 1);
		} else {
			this.viewMonth = new Date(this.viewMonth.getFullYear() - 1, this.viewMonth.getMonth(), 1);
		}
	}

	nextYear(isCompare = false): void {
		if (isCompare) {
			this.compareViewMonth = new Date(this.compareViewMonth.getFullYear() + 1, this.compareViewMonth.getMonth(), 1);
		} else {
			this.viewMonth = new Date(this.viewMonth.getFullYear() + 1, this.viewMonth.getMonth(), 1);
		}
	}

	selectPreset(preset: QuickPreset): void {
		const range = preset.getRange();
		this.tempStart = range.start;
		this.tempEnd = range.end;
		this.activePreset = preset.key;
		this.viewMonth = new Date(range.start.getFullYear(), range.start.getMonth(), 1);
	}

	selectDate(dayDate: Date, isCompare = false): void {
		if (isCompare) {
			if (!this.compareTempEnd && this.compareTempStart) {
				if (dayDate < this.compareTempStart) {
					this.compareTempEnd = this.compareTempStart;
					this.compareTempStart = dayDate;
				} else {
					this.compareTempEnd = dayDate;
				}
			} else {
				this.compareTempStart = dayDate;
				this.compareTempEnd = null;
				this.compareHoverDate = dayDate;
			}
			return;
		}

		this.activePreset = null;
		if (!this.tempEnd && this.tempStart) {
			if (dayDate < this.tempStart) {
				this.tempEnd = this.tempStart;
				this.tempStart = dayDate;
			} else {
				this.tempEnd = dayDate;
			}
		} else {
			this.tempStart = dayDate;
			this.tempEnd = null;
			this.hoverDate = dayDate;
		}
	}

	onDayHover(date: Date, isCompare = false): void {
		if (isCompare) {
			if (!this.compareTempEnd && this.compareTempStart) {
				this.compareHoverDate = date;
			}
		} else if (!this.tempEnd && this.tempStart) {
			this.hoverDate = date;
		}
	}

	applyRange(): void {
		const finalStart = this.tempStart;
		const finalEnd = this.tempEnd || this.tempStart;
		const presetObj = this.quickPresets.find((p) => p.key === this.activePreset);
		const label = presetObj ? this.translate.instant(presetObj.labelKey) : formatRangeText(finalStart, finalEnd, this.lang);

		this.dateRangeChange.emit({ startDate: finalStart, endDate: finalEnd, label, presetKey: this.activePreset || 'custom' });

		if (this.compareOption.type === 'previous_period') {
			const newPrev = calculatePreviousPeriod(finalStart, finalEnd, this.lang, this.vsLabel);
			this.compareOptionChange.emit({ type: 'previous_period', label: newPrev.label, startDate: newPrev.start, endDate: newPrev.end });
		}

		this.isOpen = false;
	}

	cancelRange(): void {
		this.isOpen = false;
	}

	selectCompareOption(type: 'previous_period' | 'same_period_last_year' | 'none'): void {
		if (type === 'previous_period') {
			this.compareOptionChange.emit({ type, label: this.prevPeriod.label, startDate: this.prevPeriod.start, endDate: this.prevPeriod.end });
		} else if (type === 'same_period_last_year') {
			this.compareOptionChange.emit({ type, label: this.lastYearPeriod.label, startDate: this.lastYearPeriod.start, endDate: this.lastYearPeriod.end });
		} else {
			this.compareOptionChange.emit({ type: 'none', label: this.translate.instant('ANALYTICS_DATE_PICKER.NO_COMPARISON') });
		}
		this.isCompareOpen = false;
	}

	openCustomCompare(): void {
		const base = this.compareOption.startDate || this.prevPeriod.start;
		this.compareTempStart = this.compareOption.startDate || this.prevPeriod.start;
		this.compareTempEnd = this.compareOption.endDate || this.prevPeriod.end;
		this.compareHoverDate = null;
		this.compareViewMonth = new Date(base.getFullYear(), base.getMonth(), 1);
		this.isCustomComparePickerOpen = true;
	}

	backToCompareOptions(): void {
		this.isCustomComparePickerOpen = false;
	}

	cancelCustomCompare(): void {
		this.isCustomComparePickerOpen = false;
		this.isCompareOpen = false;
	}

	applyCustomCompare(): void {
		const finalStart = this.compareTempStart;
		const finalEnd = this.compareTempEnd || this.compareTempStart;
		this.compareOptionChange.emit({ type: 'custom', label: `${this.vsLabel} ${formatRangeText(finalStart, finalEnd, this.lang)}`, startDate: finalStart, endDate: finalEnd });
		this.isCustomComparePickerOpen = false;
		this.isCompareOpen = false;
	}

	// SETTE redesign (CLIPPING-598): "избран ден bg-blue text-on-blue rounded-full; опсег
	// bg-blue-tint" - start/end are the solid pill, everything between is the flat tint.
	dayCellClasses(day: CalendarDay, isCompare: boolean): string {
		const { isStart, isEnd, isInRange } = this.dayStatus(day.date, isCompare);
		if (isInRange && day.isCurrentMonth) {
			if (isStart && isEnd) return 'bg-blue text-on-blue font-medium rounded-full z-10';
			if (isStart) return 'bg-blue text-on-blue font-medium rounded-l-full z-10';
			if (isEnd) return 'bg-blue text-on-blue font-medium rounded-r-full z-10';
			return 'bg-blue-tint text-blue font-medium rounded-none';
		}
		if (!day.isCurrentMonth) return 'text-ink-subtle/60 hover:text-ink-subtle hover:bg-surface-gray';
		return 'hover:bg-surface-gray text-ink-muted';
	}

	private dayStatus(date: Date, isCompare: boolean): { isStart: boolean; isEnd: boolean; isInRange: boolean } {
		const start = isCompare ? this.compareTempStart : this.tempStart;
		const end = isCompare ? this.compareTempEnd || this.compareHoverDate : this.tempEnd || this.hoverDate;

		const time = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
		const sTime = start ? new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime() : null;
		const eTime = end ? new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime() : null;

		if (sTime === null) return { isStart: false, isEnd: false, isInRange: false };

		const minTime = eTime ? Math.min(sTime, eTime) : sTime;
		const maxTime = eTime ? Math.max(sTime, eTime) : sTime;

		return { isStart: time === minTime, isEnd: time === maxTime, isInRange: time >= minTime && time <= maxTime };
	}

	private buildMonthDays(baseMonth: Date): MonthData {
		const year = baseMonth.getFullYear();
		const month = baseMonth.getMonth();

		const firstDayOfMonth = new Date(year, month, 1);
		const lastDayOfMonth = new Date(year, month + 1, 0);

		let startDayOfWeek = firstDayOfMonth.getDay() - 1;
		if (startDayOfWeek === -1) startDayOfWeek = 6;

		const days: CalendarDay[] = [];

		const prevMonthLastDay = new Date(year, month, 0).getDate();
		for (let i = startDayOfWeek - 1; i >= 0; i--) {
			days.push({ date: new Date(year, month - 1, prevMonthLastDay - i), isCurrentMonth: false });
		}

		const totalDays = lastDayOfMonth.getDate();
		for (let i = 1; i <= totalDays; i++) {
			days.push({ date: new Date(year, month, i), isCurrentMonth: true });
		}

		const remaining = 42 - days.length;
		for (let i = 1; i <= remaining; i++) {
			days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
		}

		return { year, month, days };
	}
}
